import React from "react";
import InfiniteScroll from "react-infinite-scroll-component";
import {Alert, Button, InputAdornment, Paper, Popover, Skeleton, TextField} from "@mui/material";
import {SearchOutlined} from "@mui/icons-material";
import {RichTreeView} from "@mui/x-tree-view/RichTreeView";
import {useInsightSupplierListQuery} from "~/pages/chat-agent/hooks/useInsightSupplierListQuery.ts";
import type {SupplierPickerSelection, SupplierTreeItem} from
    "~/pages/chat-agent/components/AgentTemplates/menus/SupplierTree.ts";
import {buildSupplierTree, getSupplierPickerSelection} from
    "~/pages/chat-agent/components/AgentTemplates/menus/SupplierTree.ts";
import {useSetSupplierMutation} from "../useContractLinksQueries.ts";

/** Same as the chat picker, so typing feels identical. */
const SEARCH_DEBOUNCE_MS = 300;
const SCROLL_ID = 'supplier-match-scroll';

type Props = {
    bagId: number;
    contractId: number;
    /** Prefills the search with what the parser extracted. */
    contractSupplierName: string;
    /** Custom opener (e.g. the tile's pencil); defaults to a "Find supplier" button. */
    renderTrigger?: (open: (event: React.MouseEvent<HTMLElement>) => void) => React.ReactNode;
};

/**
 * Resolve a contract's supplier against the spend data — the chat agent's `/`
 * supplier experience, single-select: the same endpoint and tree
 * (`useInsightSupplierListQuery` + `buildSupplierTree`), where a **parent
 * group** node matches at parent level and a **child** node at supplier level.
 * That distinction is the whole point here: it decides which transactions the
 * matcher will consider.
 */
export const SupplierMatchPicker: React.FC<Props> = ({bagId, contractId, contractSupplierName, renderTrigger}) => {
    const [anchor, setAnchor] = React.useState<HTMLElement | null>(null);
    const [search, setSearch] = React.useState(contractSupplierName);
    const [debounced, setDebounced] = React.useState(contractSupplierName);
    const [picked, setPicked] = React.useState<SupplierPickerSelection | null>(null);
    const setSupplier = useSetSupplierMutation();

    React.useEffect(() => {
        const timer = setTimeout(() => setDebounced(search.trim()), SEARCH_DEBOUNCE_MS);
        return () => clearTimeout(timer);
    }, [search]);

    const query = useInsightSupplierListQuery(anchor ? bagId : undefined, debounced);
    const rows = React.useMemo(
        () => query.data?.pages.flatMap(page => page.results) ?? [],
        [query.data],
    );
    const items = React.useMemo(() => buildSupplierTree(rows), [rows]);
    const byId = React.useMemo(() => {
        const map = new Map<string, SupplierTreeItem>();
        for (const group of items) {
            map.set(group.id, group);
            for (const child of group.children ?? []) map.set(child.id, child);
        }
        return map;
    }, [items]);

    const close = () => {
        setAnchor(null);
        setPicked(null);
    };

    const confirm = () => {
        if (!picked) return;
        setSupplier.mutate(
            // The tree's "child" is a supplier-level match; a group is parent level.
            {bagId, contractId, level: picked.variant === 'parent' ? 'parent' : 'supplier', name: picked.name},
            {onSuccess: close},
        );
    };

    const open = (event: React.MouseEvent<HTMLElement>) => setAnchor(event.currentTarget);

    return (
        <>
            {renderTrigger
                ? renderTrigger(open)
                : (
                    <Button size="small" variant="outlined" onClick={open}>
                        Find supplier
                    </Button>
                )}

            <Popover
                open={Boolean(anchor)}
                anchorEl={anchor}
                onClose={close}
                anchorOrigin={{vertical: 'bottom', horizontal: 'left'}}
                slotProps={{paper: {className: 'cl-supplier-picker'}}}>
                <Paper elevation={0} className="cl-supplier-picker-body">
                    <TextField
                        size="small"
                        fullWidth
                        autoFocus
                        placeholder="Search suppliers…"
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                        slotProps={{input: {
                            startAdornment: (
                                <InputAdornment position="start"><SearchOutlined fontSize="small"/></InputAdornment>
                            ),
                        }}}
                    />
                    <p className="cl-picker-hint">
                        From supplier normalization — pick a <b>group</b> to match at parent level,
                        or a supplier under it to match that one.
                    </p>

                    {setSupplier.error && <Alert severity="error">{setSupplier.error.message}</Alert>}

                    <div id={SCROLL_ID} className="cl-picker-scroll">
                        {query.isPending && <Skeleton variant="rectangular" height={180}/>}
                        {!query.isPending && items.length === 0 && (
                            <p className="cd-empty">No suppliers match “{debounced}”.</p>
                        )}
                        <InfiniteScroll
                            dataLength={rows.length}
                            next={() => void query.fetchNextPage()}
                            hasMore={Boolean(query.hasNextPage)}
                            loader={<Skeleton height={28}/>}
                            scrollableTarget={SCROLL_ID}
                            scrollThreshold="80px">
                            <RichTreeView
                                items={items}
                                selectedItems={picked ? findId(byId, picked) : null}
                                onSelectedItemsChange={(_e, id) => {
                                    const item = id ? byId.get(id) : undefined;
                                    setPicked(item ? getSupplierPickerSelection(item) ?? null : null);
                                }}
                            />
                        </InfiniteScroll>
                    </div>

                    <div className="cl-picker-footer">
                        <span className="cl-picker-choice">
                            {picked
                                ? <>Match at <b>{picked.variant === 'parent' ? 'parent' : 'supplier'} level</b>: {picked.name}</>
                                : 'Select a supplier or a parent group'}
                        </span>
                        <Button size="small" onClick={close}>Cancel</Button>
                        <Button size="small" variant="contained" disabled={!picked || setSupplier.isPending}
                                onClick={confirm}>
                            {setSupplier.isPending ? 'Saving…' : 'Match supplier'}
                        </Button>
                    </div>
                </Paper>
            </Popover>
        </>
    );
};

/** The tree is keyed by node id; the selection carries only a name + variant. */
function findId(byId: Map<string, SupplierTreeItem>, picked: SupplierPickerSelection): string | null {
    for (const [id, item] of byId) {
        const selection = getSupplierPickerSelection(item);
        if (selection && selection.variant === picked.variant && selection.name === picked.name) return id;
    }
    return null;
}
